import { useState, useCallback } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { AdPlacement } from '@/components/layout/AdPlacement';
import { HeroSection } from '@/components/sections/HeroSection';
import { FeaturesSection } from '@/components/sections/FeaturesSection';
import { FAQSection } from '@/components/sections/FAQSection';
import { BMIEducationSection } from '@/components/sections/BMIEducationSection';
import { AffiliateCTA } from '@/components/sections/AffiliateCTA';
import { BMICalculator } from '@/components/calculator/BMICalculator';
import { BMIResults } from '@/components/calculator/BMIResults';
import { HistoryPanel } from '@/components/calculator/HistoryPanel';
import { Toaster } from '@/components/ui/sonner';
import { useAuth } from '@/context/AuthContext';
import SEO from '@/components/SEO';

export const HomePage = () => {
  const { isAuthenticated } = useAuth();
  const [results, setResults] = useState(null);
  const [historyRefresh, setHistoryRefresh] = useState(0);

  const handleCalculate = useCallback((data) => {
    setResults(data);
    // Scroll to results
    setTimeout(() => {
      const resultsEl = document.getElementById('results');
      if (resultsEl) {
        resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, []);

  const handleSave = useCallback(() => {
    setHistoryRefresh(prev => prev + 1);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="BMI Calculator & Fitness Health Tools"
        description="Free BMI calculator with athlete mode, body composition insights, and health tracking tools. Calculate BMI accurately and understand your fitness health."
        canonical="/"
      />
      <Header />
      
      {/* Hero Section */}
      <HeroSection />
      
      {/* Top Ad Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <AdPlacement type="leaderboard" className="mx-auto" />
      </div>

      {/* Main Calculator Section */}
      <section id="calculator" className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Sidebar - Ad */}
            <div className="hidden lg:block lg:col-span-2">
              <div className="sticky top-24">
                <AdPlacement type="rectangle" />
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-8 space-y-8">
              {/* Section Header */}
              <div className="text-center">
                <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                  Calculate Your <span className="gradient-text">BMI</span>
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Enter your details below to get your Body Mass Index, health metrics, and personalized athlete insights.
                </p>
              </div>

              {/* Calculator */}
              <BMICalculator onCalculate={handleCalculate} />

              {/* In-content Ad */}
              {!results && (
                <AdPlacement type="inline" className="w-full" />
              )}

              {/* Results */}
              {results && (
                <div id="results">
                  <BMIResults 
                    results={results} 
                    onSave={handleSave}
                    isAuthenticated={isAuthenticated}
                    showSignupPrompt={true}
                  />
                  
                  {/* Affiliate Products - HIDDEN until agreements are in place
                  <AffiliateCTA 
                    bmiCategory={results.bmi} 
                    athleteMode={results.athleteMode} 
                  />
                  */}
                </div>
              )}
            </div>

            {/* Right Sidebar - Ad */}
            <div className="hidden lg:block lg:col-span-2">
              <div className="sticky top-24">
                <AdPlacement type="rectangle" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <FeaturesSection />

      {/* History Section */}
      <section id="history" className="py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Track Your <span className="gradient-text">Progress</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              View your calculation history and track your BMI trends over time. All data is stored locally on your device.
            </p>
          </div>
          
          <HistoryPanel refreshTrigger={historyRefresh} />
        </div>
      </section>

      {/* BMI Education Section - SEO Content */}
      <BMIEducationSection />

      {/* In-content Ad */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AdPlacement type="inline" className="w-full" />
      </div>

      {/* FAQ Section */}
      <FAQSection />

      {/* Footer */}
      <Footer />

      {/* Toast Notifications */}
      <Toaster position="top-right" />
    </div>
  );
};

export default HomePage;
